import React from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { TravelRequestBadge } from "./TravelRequestBadge";
import { TravelRequestButton } from "./TravelRequestButton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn, formatBudget, formatTravelDates } from "@/components/utils/utils";
import { getAvatarColor, getInitials } from "@/components/utils/avatar-colors";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";

interface TravelRequestsTableProps {
  travelRequest: TravelRequest[];
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onViewRequest: (requestId: string) => void;
  onCreateItinerary?: (requestId: string) => void;
}

export const TravelRequestsTable: React.FC<TravelRequestsTableProps> = ({
  travelRequest,
  currentPage,
  itemsPerPage,
  onPageChange,
  onViewRequest,
  onCreateItinerary,
}) => {
  // Logica paginazione
  const totalPages = Math.ceil(travelRequest.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedtravelRequest = travelRequest.slice(startIndex, endIndex);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Destinazione</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Persone</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Contatto</TableHead>
                <TableHead>Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedtravelRequest.map((trip) => (
                <TableRow key={trip.id}>
                  <TableCell>
                    <div className="font-medium text-gray-900">
                      {trip.destinations.length > 0 ? trip.destinations.join(", ") : "Destinazione da definire"}
                    </div>
                    {trip.departure_point && (
                      <div className="text-sm text-gray-500">Da: {trip.departure_point}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {formatTravelDates(trip.travel_start_date, trip.travel_end_date, { fallbackText: "Date da definire" })}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {trip.total_guests || "N/A"}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {formatBudget(trip.budget_min, trip.budget_max)}
                  </TableCell>
                  <TableCell>
                    <TravelRequestBadge travelRequest={trip} variant="table" />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className={`text-xs text-white ${getAvatarColor(trip.contact_email)}`}>
                          {getInitials(trip.contact_first_name, trip.contact_last_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {trip.contact_first_name} {trip.contact_last_name}
                        </div>
                        <div className="text-sm text-gray-500">{trip.contact_email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <TravelRequestButton
                      travelRequest={trip}
                      onClick={onViewRequest}
                      onCreateItinerary={onCreateItinerary}
                      variant="table"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Paginatore */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Mostrando {startIndex + 1}-{Math.min(endIndex, travelRequest.length)} di {travelRequest.length} risultati
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => onPageChange(currentPage - 1)}
                  className={cn(
                    "cursor-pointer",
                    currentPage === 1 && "pointer-events-none opacity-50"
                  )}
                />
              </PaginationItem>

              {/* Pagine numeriche */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Mostra solo alcune pagine per evitare overflow
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => onPageChange(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                } else if (
                  page === currentPage - 2 ||
                  page === currentPage + 2
                ) {
                  return (
                    <PaginationItem key={page}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }
                return null;
              })}

              <PaginationItem>
                <PaginationNext
                  onClick={() => onPageChange(currentPage + 1)}
                  className={cn(
                    "cursor-pointer",
                    currentPage === totalPages && "pointer-events-none opacity-50"
                  )}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};
